import {Locations} from '../api/locations';
import IPagedResponse from '../interfaces/ipaged-response';
import ILocationSearch from '../interfaces/ilocation-search';
import ILocation from '../interfaces/ilocation';
import ILocationSearchType from '../interfaces/ilocation-search-type';
import {isAndroid, isIOS, isNativeApp} from '../utils/browser';
import {imgATM, imgATMSelected, imgBranch, imgBranchSelected, imgCurrentLocation} from '../utils/img-url';

declare const Alkami: any;

export default {
    // VM
    async getLocationSearchTypesAsync({commit}: any) {
        const api = new Locations();
        let response = [] as ILocationSearchType[];
        try {
            response = await api.getLocationTypes();
        } catch (e) {
            console.log(e);
        } finally {
            commit('setLocationSearchTypes', response);
        }
    },
    async getFullStateName({ }: any, stateAbbreviation: string): Promise<string> {
      const api = new Locations();
      let response = stateAbbreviation;
        try {
            response = await api.getFullStateName(stateAbbreviation);
        } catch (e) {
            console.error('Error retrieving full state name.',e);
        }
        return response;
    },
    async getMobileMaxSearchResults({state, commit}: any) {
      const api = new Locations();
      let response = state.PageSize;
      try {
          response = await api.getMobileMaxSearchResults();
      } catch (e) {
          console.error('Error retrieving mobile max search results.', e);
      }
      commit('setPageSize', response);
    },
    async getCurrentUserAsync({ state, commit }: any) {
        const api = new Locations();
        let response = state.UserId;
        try {
            response = await api.getCurrentUserAsync();
        } catch (e) {
            console.error('Error retrieving mobile max search results.', e);
        }
        commit('setUserId', response);
    },
    // Maps
    async initGMapComponentsAsync({dispatch, commit, state}: any, version: string) {
        const map = new google.maps.Map(document.getElementById('map_canvas') as HTMLElement, {
            zoom: 8,
            mapTypeId: google.maps.MapTypeId.ROADMAP
        });
        if (!version.includes('mobile')) {
            const autocomplete = new google.maps.places.Autocomplete(
                document.getElementById('search_text') as HTMLInputElement, {
                    fields: ['formatted_address', 'geometry', 'name'],
                    types: ['geocode']
                });
            autocomplete.bindTo('bounds', map);
            autocomplete.addListener('place_changed', async () => {
                const place = autocomplete.getPlace();
                commit('setShowSearchHistory', false);
                commit('setSearchText', place.formatted_address);
                commit('addToSearchHistory', place.formatted_address);
                await dispatch('getLatLngFromAddressAsync', place.formatted_address);
                await dispatch('getFiByGeoLocationsAsync');
            });
            commit('setAutoComplete', autocomplete);
        }
        // Show Current Location Control
        const currentControlDiv = document.createElement('div');
        // Set CSS for the control border.
        const controlUI = document.createElement('div');
        controlUI.className = 'current-location-container';
        controlUI.style.backgroundColor = 'white';
        controlUI.style.borderStyle = 'solid';
        controlUI.style.borderWidth = '1px';
        controlUI.style.cursor = 'pointer';
        controlUI.style.textAlign = 'center';
        controlUI.title = Alkami.Localization.SiteText['Locations.Javascript.ClickToCurrentLocation'];

        const controlText = document.createElement('div');
        controlText.className = 'current-location-text';
        controlText.style.fontFamily = 'Arial,sans-serif';
        controlText.style.fontSize = '13px';
        controlText.style.paddingLeft = '4px';
        controlText.style.paddingRight = '4px';
        controlText.innerHTML = Alkami.Localization.SiteText['Locations.Javascript.WhereAmI'];
        controlUI.appendChild(controlText);

        controlUI.addEventListener('click', () => {
            if (state.CurrentUserLatitude && state.CurrentUserLongitude) {
                const marker = new google.maps.Marker({
                    map,
                    animation: google.maps.Animation.DROP,
                    icon: imgCurrentLocation,
                    position: new google.maps.LatLng(state.CurrentUserLatitude, state.CurrentUserLongitude),
                    zIndex: 100002
                });
                const markerBounds = new google.maps.LatLngBounds(marker.getPosition());
                google.maps.event.trigger(map, 'resize');
                map.setCenter(markerBounds.getCenter());
                map.fitBounds(markerBounds);
                dispatch('setCurrentLocationMarker', marker);
            } else {
                alert(Alkami.Localization.SiteText['Locations.Javascript.GeoCodeNotSuccessful']);
            }
        });
        currentControlDiv.appendChild(controlUI);
        map.controls[google.maps.ControlPosition.TOP_RIGHT].push(currentControlDiv);
        commit('setGMap', map);
        await dispatch('getLocationSearchHistory');
        if (version.includes('desktop') || version.includes('UseMyLocation')) {
            await dispatch('getMapCurrentPositionAsync');
        } else if (version.includes('mobile-ByAddress')) {
            await dispatch('getFiByGeoLocationsAsync');
        }
        state.IsRecentHistorySearch && commit('setShowSearchHistory', false);
    },
    async getMapCurrentPositionAsync({dispatch}: any) {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => dispatch('getCurrentPositionSuccess', position),
                (error) => dispatch('getCurrentPositionError', error),
                {
                    enableHighAccuracy: true,
                    timeout: 10000,
                    maximumAge: 0
                }
            );
        } else {
            alert(Alkami.Localization.SiteText['Locations.Javascript.GeolocationNotSupportedByBrowser']);
        }
    },
    async getCurrentPositionSuccess({dispatch, commit}: any, position: GeolocationPosition) {
        commit('setCurrentUserCoords', position.coords);
        commit('setShowMyLocation', false);
        await dispatch('getFiByGeoLocationsAsync');
    },
    getCurrentPositionError(context: any, error: ErrorCallback) {
        // User blocked access to their current location or their current location could not be determined.
        console.error('Error retrieving current position.', error);
    },
    async getFiByGeoLocationsAsync({commit, state, getters}: any) {
        let response = {} as IPagedResponse;
        const api = new Locations();
        commit('setIsSearching', false);
        commit('setShowFilterButton', true);
        try {
            response = await api.getLocations({
                Latitude: state.IsRecentHistorySearch ? state.SearchHistoryCoords[state.SearchHistoryIndex].latitude : state.CurrentUserLatitude,
                Longitude: state.IsRecentHistorySearch ? state.SearchHistoryCoords[state.SearchHistoryIndex].longitude : state.CurrentUserLongitude,
                LocationTypes: getters.getCheckedLocationSearchTypeValues
            } as ILocationSearch, state.CurrentPageNumber, state.PageSize);
            if (response.Data.length > 0 || response.Data.length == 0) {
                state.IsLoading = false;
            }
        } catch (e) {
            console.error('Error retrieving locations via geolocation.', e);
            state.IsRecentHistorySearch && commit('setSearchText', "");
            commit('setIsSearching', false);
            commit('setIsRecentHistorySearch', false);
        }
        response.Data.forEach(function (location: ILocation) {
            if (isIOS()) {
                location.MapUrl = 'https://maps.apple.com/?daddr=' + location.Latitude + ',' + location.Longitude + (isNativeApp() ? '&target=native' : '');
            } else if (isAndroid()) {
                location.MapUrl = 'geo:' + location.Latitude + ',' + location.Longitude + '?q=' + location.Latitude + ',' + location.Longitude + '(' + encodeURIComponent(location.Name) + ')';
            } else {
                location.MapUrl = 'https://maps.google.com/maps?q=loc:' + location.Latitude + ',' + location.Longitude;
            }
        });
        commit('setLocations', response.Data);
        commit('setPageSize', response.PageSize);
        commit('setCurrentPageNumber', response.PageNumber);
        commit('setTotalSearchResults', response.TotalResults);
        commit('setTotalPages', null);
        commit('setHasSearched', true);
        if (state.IsRecentHistorySearch) {
            commit('setCurrentUserLatitude', state.SearchHistoryCoords[state.SearchHistoryIndex].latitude);
            commit('setCurrentUserLongitude', state.SearchHistoryCoords[state.SearchHistoryIndex].longitude);
        }
        commit('setIsRecentHistorySearch', false);
        commit('setShowSearchHistory', false);
        commit('setSearchHistoryIndex', null);
    },
    async updateGMapAsync({ dispatch, commit, state }: any, version: string) {
        if (state.Locations) {
            state.GMap.setCenter(new google.maps.LatLng(state.CurrentUserLatitude, state.CurrentUserLongitude));
        }
        if (state.LocationMarkers) {
            state.LocationMarkers.forEach((m: google.maps.Marker) => m.setMap(null));
            commit('setLocationMarkers', null);
        }
        const markers: google.maps.Marker[] = [];
        const markerBounds = new google.maps.LatLngBounds(new google.maps.LatLng(state.CurrentUserLatitude, state.CurrentUserLongitude));
        for (let i = 0; i < state.Locations.length; i += 1) {
            const location = state.Locations[i];
            const coords = new google.maps.LatLng(location.Latitude, location.Longitude);
            const marker = new google.maps.Marker({
                map: state.GMap,
                animation: google.maps.Animation.DROP,
                title: location.Name,
                position: coords,
              icon: (location.LocationType === 'branch') ? ((i === state.SelectedIndex || i === state.FocusedIndex) ? imgBranchSelected : imgBranch) : ((i === state.SelectedIndex || i === state.FocusedIndex) ? imgATMSelected : imgATM),
                zIndex: (i === 0) ? 100001 : 100000
            });
            // @ts-ignore
            markerBounds.extend(marker.getPosition());
            marker.addListener('click', () => {
                // @ts-ignore
                if (version === 'mobile') {
                    commit('setEmblaScrollToIndex', state.LocationMarkers.indexOf(marker));
                    dispatch('focusLocation', state.LocationMarkers.indexOf(marker));
                } else {
                    commit('setSelectedLocation', state.Locations[markers.indexOf(marker)]);
                    dispatch('selectLocation', state.LocationMarkers.indexOf(marker));
                }
            });
            markers.push(marker);
        }
        commit('setLocationMarkers', markers);
        markerBounds.extend(new google.maps.LatLng(markerBounds.getNorthEast().lat() + 0.02, markerBounds.getNorthEast().lng()));
        markerBounds.extend(new google.maps.LatLng(markerBounds.getSouthWest().lat() - 0.02, markerBounds.getSouthWest().lng()));
        markerBounds.extend(new google.maps.LatLng(markerBounds.getNorthEast().lat() + 0.02, markerBounds.getNorthEast().lng()));
        markerBounds.extend(new google.maps.LatLng(markerBounds.getSouthWest().lat() - 0.02, markerBounds.getSouthWest().lng()));
        google.maps.event.trigger(state.GMap, 'resize');
        state.GMap.setCenter(markerBounds.getCenter());
        state.GMap.fitBounds(markerBounds);
    },

    setMarkerIconSelected({ commit, getters }: any, index: number) {
        var marker = getters.getLocationMarkers[index];
        if (marker) {
            const icon = marker.getIcon().toString();
            commit('setCurrentLocationMarker', marker);
            marker.setZIndex(marker.getZIndex() === 100001 ? 100000 : 100001);
            marker.setIcon(icon.includes('branch') ? imgBranchSelected : imgATMSelected);
            commit('setLocationMarker', marker, index);
        }
    },
    setMarkerIconUnselected({ commit, getters }: any, index: number) {
        var marker = getters.getLocationMarkers[index];
        if (marker) {
            const icon = marker.getIcon().toString();
            marker.setZIndex(marker.getZIndex() === 100001 ? 100000 : 100001);
            marker.setIcon(icon.includes('branch') ? imgBranch : imgATM);
            commit('setLocationMarker', marker, index);
        }
    },
    focusLocation({ dispatch, commit }: any, index: number) {
        // @ts-ignore
        dispatch('unfocusLocation');
        dispatch('setMarkerIconSelected', index);
        commit('setFocusedIndex', index);
    },
    unfocusLocation({ dispatch, commit, getters }: any) {
        // @ts-ignore
        var index = getters.getFocusedIndex;
        if (getters.getSelectedIndex != index) {
            dispatch('setMarkerIconUnselected', index);
        }
        commit('setFocusedIndex', -1);
    },
    selectLocation({ dispatch, commit }: any, index: number) {
        // @ts-ignore
        dispatch('deselectLocation');
        dispatch('setMarkerIconSelected', index);
        commit('setSelectedIndex', index);
    },
    deselectLocation({ dispatch, commit, getters }: any) {
        // @ts-ignore
        var index = getters.getSelectedIndex;
        if (getters.getFocusedIndex != index) {
            dispatch('setMarkerIconUnselected', index);
        }
        commit('setSelectedIndex', -1);
    },

    async getLatLngFromAddressAsync({dispatch, commit}: any, address: string) {
        const geocoder = new google.maps.Geocoder();
        await geocoder.geocode({address}, function (results: any, status: google.maps.GeocoderStatus) {
            if (status === google.maps.GeocoderStatus.OK && results !== null) {
                commit('setCurrentUserLatitude', results[0].geometry.location.lat());
                commit('setCurrentUserLongitude', results[0].geometry.location.lng());
                commit('addToSearchHistoryAddress', address);
                commit('addToSearchHistoryCoords', results[0].geometry.location);
            } else {
                console.log('Error retrieving geocode.', results);
            }
        });
    },
    // Searching
    getLocationSearchHistory({commit, state}: any) {
        if (state.IsSearchHistorySupported) {
            const localStorageHistory: string | null = localStorage.getItem('locationSearchHistory');
            if (localStorageHistory !== null) {
                commit('addToSearchHistory', JSON.parse(localStorageHistory));
                localStorage.setItem('locationSearchHistory', JSON.stringify(localStorageHistory));
            }
        }
    },
    async getWidgetTitleAsync({ state, commit }: any) {
        const api = new Locations();
        let response = '';
        try {
            response = await api.getWidgetTitle();
        } catch (e) {
            console.error('Error retrieving widget title.', e);
        }
        commit('setWidgetTitle', response);
    }
};